package org.example.zone360.pages.dashboard.recentHighlight

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WordBreak
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.example.zone360.utils.getClubSession
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.zone360.components.*
import org.example.zone360.models.SessionData
import org.example.zone360.pages.dashboard.SelectedBox
import org.example.zone360.pages.dashboard.recentAssessment.AssessmentSessionItem
import org.example.zone360.theme.toSitePalette
import org.example.zone360.utils.AppGlobal
import org.example.zone360.utils.mediaManagerUtils.AudioManager
import org.example.zone360.utils.Constants
import org.example.zone360.utils.RecentHighlights
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.w3c.dom.Audio
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL

@Composable
fun RecentHighlight(
    clubSessionList: List<SessionData>,
    breakpoint: Breakpoint,
) {
    var isSelected by remember { mutableStateOf(RecentHighlights.RecentSession) }
    val sitePalette = ColorMode.current.toSitePalette()
    var searchText by remember { mutableStateOf("") }
    var searchAssessmentText by remember { mutableStateOf("") }
    val playerSessionList = remember { mutableStateListOf<SessionData>() }

    // Parent controls WHICH item plays + holds a ref to pause previous
    var currentlyPlayingId by remember { mutableStateOf<String?>(null) }
    var loadingSessionId by remember { mutableStateOf<String?>(null) }
    val currentAudioRef = remember { mutableStateOf<Audio?>(null) }

    // Load assessment list
    LaunchedEffect(AppGlobal.selectedSeasonId) {
        if (AppGlobal.selectedSeasonId.isEmpty()) return@LaunchedEffect
        val playerUser = getClubSession(
            organization = AppGlobal.userData.organization,
            sessionType = "player assessment",
            domainType = "soccer",
            language = "en",
            seasonID = AppGlobal.selectedSeasonId
        )
        val sortedData = playerUser.data.sortedByDescending { sessionData ->
            val parts = sessionData.sessionId.removeSuffix(".aac").split("_")
            parts.last()
        }
        playerSessionList.clear()
        playerSessionList.addAll(sortedData)
    }

    val filteredList = clubSessionList.filter {
        it.sessionId.contains(searchText, ignoreCase = true)
    }
    val filteredAssessmentList = playerSessionList.filter {
        it.playerName.contains(searchAssessmentText, ignoreCase = true)
    }

    Box(
        modifier = Modifier.fillMaxWidth()
            .then(if (window.screen.width > 765) Modifier.height(915.px) else Modifier.maxHeight(915.px))
            .margin(topBottom = if (breakpoint <= Breakpoint.MD) 5.px else 0.px)
            .backgroundColor(sitePalette.modalBackgroundColor.rgb)
            .borderRadius(15.px)
            .padding(20.px)
    ) {
        Column(Modifier.fillMaxSize()) {
            Row(
                modifier = Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    modifier = Modifier
                        .margin(topBottom = 10.px)
                        .borderRadius(6.px)
                        .backgroundColor(sitePalette.modalBackgroundColor.rgb)
                        .padding(leftRight = 5.px, topBottom = 4.px)
                        .border(1.px, LineStyle.Solid, Colors.LightGray),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    listOf(
                        RecentHighlights.RecentSession to if (breakpoint >= Breakpoint.MD) "Recent Sessions" else "Sessions",
                        RecentHighlights.RecentAssessment to if (breakpoint >= Breakpoint.MD) "Recent Assessments" else "Assessments"
                    ).forEach { (type, label) ->
                        SelectedBox(
                            text = label,
                            breakpoint = breakpoint,
                            width = if (breakpoint >= Breakpoint.MD) 160.px else 95.px,
                            height = 34.px,
                            fontSize = 14.px,
                            fontWeight = 500,
                            isSelected = isSelected == type,
                            onClick = { isSelected = type }
                        )
                    }
                }
            }
            if (isSelected == RecentHighlights.RecentSession) {
                SearchBar(
                    placeholder = "Search Session...",
                    id = "highlight_Searchbar",
                    breakpoint = Breakpoint.LG,
                    onEnterClick = {},
                    onChange = { searchText = it },
                    onClick = {},
                    width = 100.percent,
                    showClearButton = false
                )
            } else {
                SearchBar(
                    placeholder = "Search Assessment...",
                    id = "highlight_Searchbar",
                    breakpoint = Breakpoint.LG,
                    onEnterClick = {},
                    onChange = { searchAssessmentText = it },
                    onClick = {},
                    width = 100.percent,
                    showClearButton = false
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth()
                    .margin(topBottom = 10.px)
                    .maxHeight(915.px)
                    .overflow(overflowY = Overflow.Auto, overflowX = Overflow.Hidden)
            ) {
                if (isSelected == RecentHighlights.RecentSession) {
                    filteredList.forEach { sessionData ->
                        SessionItem(
                            sessionData = sessionData,
                            isPlaying = (currentlyPlayingId == sessionData.sessionId),
                            isLoading = (loadingSessionId == sessionData.sessionId),
                            onPlay = { audio ->
                                // Pause previously playing audio if any
                                currentAudioRef.value?.pause()
                                loadingSessionId = sessionData.sessionId
                                // set new audio as the current ref
                                currentAudioRef.value = audio
                                currentlyPlayingId = sessionData.sessionId
                                loadingSessionId = null
                                // Let the row handle play() and progress; parent just tracks state
                            },
                            onPause = {
                                currentAudioRef.value?.pause()
                                currentlyPlayingId = null
                            },
                            breakpoint = breakpoint
                        )
                    }
                } else {
                    AssessmentSessionItem(filteredAssessmentList, breakpoint)
                }
            }
        }
    }
}

@Composable
fun SessionItem(
    sessionData: SessionData,
    isPlaying: Boolean,
    isLoading: Boolean,
    onPlay: (Audio) -> Unit,
    onPause: () -> Unit,
    breakpoint: Breakpoint
) {
    val sitePalette = ColorMode.current.toSitePalette()
    val sessionId = sessionData.sessionId
    val parts = sessionId.removeSuffix(".aac").split("_")
    val sessionName = parts.first()
    val timeStamp = parts.last()

    // One audio object per row
    val audioUrl = "${Res.Audio.audio_base_url}$sessionId"
    val audio = remember { mutableStateOf<Audio?>(null) }
    // One progress state per row
    var audioProgress by remember { mutableStateOf(0f) }
    var isDownloading by remember(sessionId) { mutableStateOf(false) }

    // Keep the slider in sync + pause on dispose
    DisposableEffect(audio.value) {  // re-runs when audio.value changes
        val currentAudio = audio.value  // capture non-null local reference

        if (currentAudio == null) {
            onDispose {}  // nothing to clean up
        }else {

            val timeUpdateListener: (org.w3c.dom.events.Event) -> Unit = {
                val d = currentAudio.duration
                if (!d.isNaN() && d > 0) {
                    audioProgress = (currentAudio.currentTime / d).toFloat()
                }
            }
            val endedListener: (org.w3c.dom.events.Event) -> Unit = {
                audioProgress = 0f
                onPause()
            }

            currentAudio.addEventListener("timeupdate", timeUpdateListener)
            currentAudio.addEventListener("ended", endedListener)

            onDispose {
                currentAudio.pause()
                currentAudio.removeEventListener("timeupdate", timeUpdateListener)
                currentAudio.removeEventListener("ended", endedListener)
            }
        }
    }

    Column(
        modifier = Modifier.fillMaxSize().padding(5.px)
    ) {
        SpanText(
            sessionName.trim()
                .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() },
            modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(500)
                .fontSize(16.px).wordBreak(WordBreak.BreakAll)
        )

        if (breakpoint < Breakpoint.MD) {
            SpanText(
                sessionData.coachName,
                modifier = Modifier.fontFamily(Constants.FONT_FAMILY)
                    .color(sitePalette.textFieldSupportiveTextColor.rgb)
                    .fontSize(13.px).margin(bottom = 3.px)
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        )
        {
            if (breakpoint >= Breakpoint.MD) {
                SpanText(
                    sessionData.coachName,
                    modifier = Modifier.fontFamily(Constants.FONT_FAMILY).width(110.px)
                        .fontWeight(400)
                        .fontSize(13.px).wordBreak(WordBreak.BreakAll)
                )
            }

            Row(modifier = Modifier.width(100.px), verticalAlignment = Alignment.CenterVertically) {
                Image(src = Res.Images.timeCircle, modifier = Modifier.size(17.px))
                CustomSpacer(5.px)
                SpanText(
                    sessionData.recordTime.toLongOrNull()
                        ?.let { (it / 1000).formatSessionMinutes() } ?: "-",
                    modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(400)
                        .margin(right = 3.px).fontSize(13.px)
                )
            }

            Row(modifier = Modifier.width(180.px), verticalAlignment = Alignment.CenterVertically) {
                Image(src = Res.Images.calender, modifier = Modifier.size(17.px))
                CustomSpacer(5.px)
                SpanText(
                    "${timeStamp.formatSessionDate()} ${timeStamp.formatSessionHoursAmPm()}",
                    modifier = Modifier.fontFamily(Constants.FONT_FAMILY).fontWeight(400)
                        .fontSize(13.px)
                )
            }

            Row(verticalAlignment = Alignment.CenterVertically) {
                CustomSpacer(6.px)
                Box(contentAlignment = Alignment.Center) {
                    if (isDownloading) {
                        ProgressIndicator(size = 20.px)
                    } else {
                        Image(
                            src = Res.Images.downloadPrimary,
                            modifier = Modifier.size(29.px).cursor(Cursor.Pointer)
                                .onClick {
                                    isDownloading = true
                                    val url = "${Res.Audio.audio_base_url}$sessionId"
                                    window.fetch(url).then { response ->
                                        response.blob().then { blob ->
                                            val objectUrl = URL.createObjectURL(blob)
                                            val link = document.createElement("a") as HTMLAnchorElement
                                            link.href = objectUrl
                                            link.download = "${sessionName}_${timeStamp}.aac"
                                            document.body?.appendChild(link)
                                            link.click()
                                            document.body?.removeChild(link)
                                            URL.revokeObjectURL(objectUrl)
                                            isDownloading = false
                                        }
                                    }
                                }
                        )
                    }
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().margin(topBottom = 4.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            when {
                isLoading -> {
                    Box(contentAlignment = Alignment.Center) {
                        Image(
                            src = Res.Images.blueCircle,
                            modifier = Modifier.size(30.px).borderRadius(100.px)
                        )
                        ProgressIndicator(10.px)
                    }
                }

                isPlaying -> {
                    Image(
                        src = Res.Images.pausebutton,
                        modifier = Modifier.size(30.px)
                            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                            .onClick {
                                audio.value?.pause()
                                onPause()
                                audioProgress = 0f
                            }
                    )
                }

                else -> {
                    Image(
                        src = Res.Images.playbutton,
                        modifier = Modifier.size(30.px)
                            .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                            .onClick {
                                val prepared = AudioManager.prepare(audioUrl)
                                audio.value = prepared
                                onPlay(prepared)   // rest of your existing logic unchanged
                                if (prepared.readyState.toInt() < 2) {
                                    prepared.load()
                                    prepared.onloadeddata = { prepared.play() }
                                } else {
                                    prepared.play()
                                }
                            }
                    )
                }
            }

            CustomSpacer(10.px)

            AudioProgressSliderCustom(
                progress = audioProgress,
                onProgressChange = { newProgress ->
                    val currentAudio = audio.value ?: return@AudioProgressSliderCustom
                    audioProgress = newProgress
                    val dur = currentAudio.duration
                    if (dur > 0) {
                        currentAudio.currentTime = dur * newProgress
                        if (isPlaying && currentAudio.paused) {
                            currentAudio.play()
                        }
                    }
                },
            )
        }

        HorizontalDivider(modifier = Modifier.color(Colors.Gray).fillMaxWidth())
    }
}

